import { lumi } from "@/lib/lumi"
import { useState, useCallback } from "react"
import toast from "react-hot-toast"

export interface RecipeOil {
  oil: string
  percentage: number
  costPerUnit: number
}

export interface SoapRecipe {
  _id?: string
  name: string
  description: string
  unit: "grams" | "ounces"
  liquidType: "water" | "goat-milk" | "aloe-juice"
  lyeType: "NaOH" | "KOH"
  superfat: number
  waterPercent: number
  lyeCalculationMode: "concentration" | "ratio"
  lyeConcentration: number
  batchSize: number
  fragrancePercent: number
  oils: RecipeOil[]
  tags: string[]
  isFavorite: boolean
  creator: string
  createdAt: string
  updatedAt: string
}

export function useRecipes() {
  const [recipes, setRecipes] = useState<SoapRecipe[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const fetchRecipes = useCallback(async (options?: {
    filter?: Record<string, unknown>
    sort?: Record<string, 1 | -1>
    limit?: number
    skip?: number
  }) => {
    setLoading(true)
    try {
      const { list, total: count } = await lumi.entities.soap_recipes.list({
        sort: { updatedAt: -1 },
        ...options,
      })
      setRecipes(list as SoapRecipe[])
      setTotal(count)
      console.log("Recipes fetched:", list.length)
    } catch (error) {
      console.error("Failed to fetch recipes:", error)
      toast.error("Failed to load recipes")
    } finally {
      setLoading(false)
    }
  }, [])

  const saveRecipe = useCallback(async (data: Omit<SoapRecipe, "_id" | "creator" | "createdAt" | "updatedAt">) => {
    setSaving(true)
    try {
      const now = new Date().toISOString()
      const user = lumi.auth.user
      const recipeData = {
        ...data,
        creator: user?.userId || "anonymous",
        createdAt: now,
        updatedAt: now,
      }
      const newRecipe = await lumi.entities.soap_recipes.create(recipeData)
      console.log("Recipe created:", newRecipe)
      toast.success("Recipe saved successfully!")
      await fetchRecipes()
      return newRecipe
    } catch (error) {
      console.error("Failed to save recipe:", error)
      toast.error("Failed to save recipe. Please try again.")
      throw error
    } finally {
      setSaving(false)
    }
  }, [fetchRecipes])

  const updateRecipe = useCallback(async (id: string, data: Partial<SoapRecipe>) => {
    if (typeof id !== "string") {
      throw new Error("ID must be a string")
    }
    setSaving(true)
    try {
      const updates = {
        ...data,
        updatedAt: new Date().toISOString(),
      }
      delete updates._id
      delete updates.creator
      delete updates.createdAt
      const updated = await lumi.entities.soap_recipes.update(id, updates)
      console.log("Recipe updated:", updated)
      toast.success("Recipe updated successfully!")
      await fetchRecipes()
      return updated
    } catch (error) {
      console.error("Failed to update recipe:", error)
      toast.error("Failed to update recipe. Please try again.")
      throw error
    } finally {
      setSaving(false)
    }
  }, [fetchRecipes])

  const deleteRecipe = useCallback(async (id: string) => {
    if (typeof id !== "string" || id === "[object Object]") {
      throw new Error("Invalid ID: must be a string")
    }
    try {
      await lumi.entities.soap_recipes.delete(id)
      console.log("Recipe deleted:", id)
      toast.success("Recipe deleted")
      await fetchRecipes()
    } catch (error) {
      console.error("Failed to delete recipe:", error)
      toast.error("Failed to delete recipe. Please try again.")
      throw error
    }
  }, [fetchRecipes])

  const toggleFavorite = useCallback(async (id: string, currentValue: boolean) => {
    try {
      await lumi.entities.soap_recipes.update(id, {
        isFavorite: !currentValue,
        updatedAt: new Date().toISOString(),
      })
      await fetchRecipes()
      toast.success(!currentValue ? "Added to favorites" : "Removed from favorites")
    } catch (error) {
      console.error("Failed to toggle favorite:", error)
      toast.error("Failed to update favorite status")
    }
  }, [fetchRecipes])

  return {
    recipes,
    total,
    loading,
    saving,
    fetchRecipes,
    saveRecipe,
    updateRecipe,
    deleteRecipe,
    toggleFavorite,
  }
}
